import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MobileStepper from "@mui/material/MobileStepper";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import SwipeableViews from "react-swipeable-views";
import { virtualize } from "react-swipeable-views-utils";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { mod } from "react-swipeable-views-core";

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

function CarouselItem({ height, src, label, scale }) {
  return (
    <Box
      sx={{
        height,
        aspectRatio: "1",
        overflow: "scroll",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={
          !!scale
            ? {
                transform: `scale(${1 + +0.5 * scale})`,
                transformOrigin: "0 0",
              }
            : { width: "100%" }
        }
      >
        <Box
          component="img"
          sx={{
            display: "block",
            overflow: "hidden",
            width: "100%",
          }}
          src={src}
          alt={label}
        />
      </Box>
    </Box>
  );
}

function SwipeableCarousel({
  height = "300px",
  images,
  viewSx = {},
  showLabel = false,
  showActionText = false,
  showActionIcon = false,
  showAction = false,
  showZoom = false,
  children,
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const [scale, setScale] = React.useState(0);
  const maxSteps = images.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => mod(prevActiveStep + 1, maxSteps));
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => mod(prevActiveStep - 1, maxSteps));
  };

  const handleStepChange = (step) => {
    setActiveStep(mod(step, maxSteps));
    setScale(0);
  };

  function slideRenderer(params) {
    const { index, key } = params;
    const item = images[mod(index, maxSteps)];
    return (
      <CarouselItem key={key} {...{ index, key, scale, height, ...item }} />
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {showLabel && (
        <Paper
          square
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            height: 50,
            pl: 2,
            bgcolor: "background.default",
          }}
        >
          <Typography>{images[activeStep].label}</Typography>
        </Paper>
      )}

      <Box
        sx={{
          borderRadius: "2px",
          position: "relative",
          ...viewSx,
        }}
      >
        <VirtualizeSwipeableViews
          axis={theme.direction === "rtl" ? "x-reverse" : "x"}
          index={activeStep}
          onChangeIndex={handleStepChange}
          disabled={showZoom && scale !== 0}
          slideRenderer={slideRenderer}
        ></VirtualizeSwipeableViews>
      </Box>

      <MobileStepper
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        sx={{
          backgroundColor: "transparent",
          "& .MuiMobileStepper-dot": { background: "#ac2710" },
          "& .MuiMobileStepper-dotActive": { background: "#dd5d08" },
        }}
        nextButton={
          showAction ? (
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === maxSteps - 1}
            >
              {showActionText && "Next"}
              {showActionIcon &&
                (theme.direction === "rtl" ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                ))}
            </Button>
          ) : (
            <span></span>
          )
        }
        backButton={
          showAction ? (
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {showActionIcon &&
                (theme.direction === "rtl" ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                ))}
              {showActionText && "Back"}
            </Button>
          ) : (
            <span></span>
          )
        }
      />
      {children && children[activeStep]}
    </Box>
  );
}

export default SwipeableCarousel;
